import _ from 'lodash'
import moment from 'moment-timezone'

import { EditableFieldProps } from '../core-field/editable-field-json-props'

export type DateTimePickerMode = 'date' | 'time' | 'datetime'

export interface UI2DateTimeFieldProps extends EditableFieldProps<string> {
  // Which components to display in the picker UI. Default: 'datetime'.
  mode?: DateTimePickerMode
  // Which components to preserve in the stored value. Allows e.g. showing only
  // a date picker while keeping an existing time component in the stored ISO string.
  valueMode?: DateTimePickerMode
  // Emits { dateTime, timezone } instead of an ISO string.
  showTimezoneInput?: boolean
  // ISO date string; days before this are disabled.
  minDate?: string
  // ISO date string; days after this are disabled.
  maxDate?: string
  // Minute increment for the time picker (iOS only). Default: 1.
  minuteStep?: number
  /**
   * Whether an existing value can be cleared from the field. Renders an
   * inline clear affordance beside the value. Default: true.
   */
  isClearable?: boolean
  /**
   * Legacy JSON-schema type piggyback used to auto-detect timezone-aware
   * fields when `showTimezoneInput` is not explicitly set. Prefer setting
   * `showTimezoneInput` on new schemas; kept for compatibility with
   * existing schemas that rely on the dataSchema.type check.
   */
  type?: any
}

export const UI2_DATE_TIME_FIELD_DEFAULT_PROPS: Partial<UI2DateTimeFieldProps> = {
  mode: 'datetime',
  isClearable: true,
}

/** moment format strings, keyed by picker mode. */
export const DATE_TIME_MODE_FORMATTERS: Record<DateTimePickerMode, string> = {
  date: 'L',
  datetime: 'L LT',
  time: 'LT',
}

const MODE_PLACEHOLDERS: Record<DateTimePickerMode, string> = {
  date: 'Select date',
  datetime: 'Select date and time',
  time: 'Select time',
}

/**
 * Default placeholder when the caller does not provide one: the standard
 * field wording ("Enter <label>") if the field is labeled, otherwise a
 * mode-appropriate fallback ("Select date and time").
 */
export function getDefaultDateTimePlaceholder(
  mode: DateTimePickerMode = 'datetime',
  label?: string,
): string {
  const trimmed = label?.trim()
  return trimmed ? `Enter ${trimmed}` : MODE_PLACEHOLDERS[mode]
}

/** The ISO date-time half of a value, whether stored as a string or `{dateTime, timezone}`. */
export function getDateTimePart(value: any): string | undefined {
  const dateTime = _.get(value, 'dateTime', value)
  return _.isString(dateTime) && dateTime.length > 0 ? dateTime : undefined
}

/** The timezone half of a `{dateTime, timezone}` value, when present. */
export function getTimezonePart(value: any): string | undefined {
  const timezone = _.get(value, 'timezone')
  return _.isString(timezone) && timezone.length > 0 ? timezone : undefined
}

/**
 * The single display formatter every platform calls, so the displayed and
 * read-only strings never disagree between NM, DW and MW.
 *
 * With `showTimezoneInput` the value is rendered *in* its stored timezone and
 * suffixed with that zone's abbreviation (`3/4/2026 9:15 AM PST`) — otherwise
 * the reader can't tell which zone an absolute instant is being shown in.
 * Returns `''` for an empty or unparseable value so callers can fall through
 * to their placeholder / empty state.
 */
export function formatDateTimeValue(
  value: any,
  mode: DateTimePickerMode = 'datetime',
  showTimezoneInput?: boolean,
): string {
  const dateTime = getDateTimePart(value)
  if (!dateTime) return ''
  const format = DATE_TIME_MODE_FORMATTERS[mode] ?? DATE_TIME_MODE_FORMATTERS.datetime
  const timezone = getTimezonePart(value)
  // Only zone-shift when the field is timezone-aware AND the value carries a
  // zone. A plain ISO string stays in the viewer's local zone, as before.
  if (showTimezoneInput && timezone) {
    const zoned = moment.tz(dateTime, moment.ISO_8601, true, timezone)
    if (!zoned.isValid()) return ''
    return `${zoned.format(format)} ${zoned.format('z')}`
  }
  const local = moment(dateTime, moment.ISO_8601, true)
  return local.isValid() ? local.format(format) : ''
}

/**
 * Emptiness for the field shell. The default `hasFieldValue` check treats any
 * object as populated, so a timezone-aware `{timezone: 'America/Denver'}` with
 * no date picked yet would read as filled and skip the empty read-only state.
 */
export function isDateTimeValueEmpty(value: unknown): boolean {
  return !getDateTimePart(value)
}

export interface DateTimeTimezoneOption {
  label: string
  value: string
}

let cachedTimezoneOptions: DateTimeTimezoneOption[] | null = null

/**
 * Every IANA zone name, labeled with its current UTC offset
 * (`America/Denver (UTC-06:00)`). Built once and cached — `moment.tz.names()`
 * is ~600 entries and the offset formatting is not free.
 *
 * Shared so the NM action sheet and the DW/MW picker popover offer the same
 * list in the same order.
 */
export function getTimezoneOptions(): DateTimeTimezoneOption[] {
  if (!cachedTimezoneOptions) {
    const now = new Date()
    cachedTimezoneOptions = moment.tz.names().map((tz) => ({
      label: `${tz} (UTC${moment.tz(now, tz).format('Z')})`,
      value: tz,
    }))
  }
  return cachedTimezoneOptions
}

/** The label `getTimezoneOptions` uses for a single zone. */
export function getTimezoneOptionLabel(timezone: string): string {
  return getTimezoneOptions().find((option) => option.value === timezone)?.label ?? timezone
}
